import React, { useState, useEffect, useRef, useCallback } from "react";
import { message } from "antd";
import { AddRect } from "./AddRect";
import SimpleButton from "components/GlobalButtons/SimpleButton";
import styled from "styled-components";
import UndoIcon from 'statics/images/undo_icon.png';

export type Rectangle = {
  startX: number;
  startY: number;
  width: number;
  length: number;
  levels: number;
  name: string;
};

const CrossDocking = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  //Positioning and moding
  const [cursorMode, setCursorMode] = useState<"pan" | "draw">("draw");
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [is3DMode, setIs3DMode] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);

  //All rectangles
  const [rectMode, setRectMode] = useState<"hub" | "shelf" | "bin">("hub");
  const [newRectangle, setNewRectangle] = useState<Rectangle | null>(null);
  const [hubsRect, setHubsRect] = useState<Rectangle[]>([]);
  const [shelvesRect, setShelvesRect] = useState<Rectangle[]>([]);
  const [binsRect, setBinsRect] = useState<Rectangle[]>([]);
  const [selectedRectangle, setSelectedRectangle] = useState<Rectangle | null>(
    null
  );
  const [parentRect, setParentRect] = useState<Rectangle | null>(null);
  const [activeInput, setActiveInput] = useState(false);

  const roundToGrid = (value: number, gridSize: number = 10) => {
    return Math.round(value / gridSize) * gridSize;
  };

  const roundedPosition = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const posX = -canvasOffset.x / 2 + e.nativeEvent.offsetX;
      const posY = -canvasOffset.y / 2 + e.nativeEvent.offsetY;
      return { positionX: roundToGrid(posX), positionY: roundToGrid(posY) };
    },
    [canvasOffset.x, canvasOffset.y]
  );

  const sortRectangles = (rectangles: Rectangle[]) => {
    return rectangles.sort((a, b) => {
      const compareWidth = a.startX + a.width - (b.startX + b.width);
      const compareDepth = a.startY + a.length - (b.startY + b.length);
      const compareLevels = a.levels - b.levels;
      const compareStartY = a.startY - b.startY;

      if (compareWidth !== 0) return compareWidth;
      if (compareDepth !== 0) return compareDepth;
      if (compareLevels !== 0) return compareLevels;
      return compareStartY;
    });
  };

  const createGradientBackground = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ) => {
    const { width, height } = canvas;
    const centerX = width / 2;
    const centerY = height / 2;
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      100,
      centerX,
      centerY,
      centerX
    );
    gradient.addColorStop(0, "hsl(220, 30%, 20%)"); // Bright center
    gradient.addColorStop(0.5, "hsl(220, 50%, 20%)"); // Metallic blue
    gradient.addColorStop(1, "hsl(220, 30%, 10%)"); // Deep black-blue edge
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "hsl(180, 100%, 25%)";
    ctx.lineWidth = 0.5;
    //Draw the grid background
    for (let i = 0; i <= canvas.height; i += 20) {
      //Draw horizontal lines
      ctx.beginPath();
      ctx.moveTo(canvas.width, i);
      ctx.lineTo(0, i);
      ctx.stroke();
    }
    for (let j = 0; j <= canvas.width; j += 20) {
      //Draw vertical lines lines
      ctx.beginPath();
      ctx.moveTo(j, 0);
      ctx.lineTo(j, canvas.height);
      ctx.stroke();
    }
  };

  const draw3DRectangles = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      width: number,
      length: number,
      levels: number,
      name: string,
      color: number,
      transparency: number
    ) => {
      // Isometric projection constants
      const isoAngle = Math.PI / 6;
      const cos = Math.cos(isoAngle);
      const sin = Math.sin(isoAngle);
      const iso = (x: number, y: number, z: number) => ({
        x: (x - z) * cos,
        y: (x + z) * sin - y - 350,
      });

      // Calculate points // p1-p4 button face // p5-p8 top face elevated by levels // Color 330 for bins
      let p1;
      let p2;
      let p3;
      let p4;
      if (color === 330) {
        p1 = iso(x, levels - 30, y);
        p2 = iso(x + width, levels - 30, y);
        p3 = iso(x + width, levels - 30, y + length);
        p4 = iso(x, levels - 30, y + length);
      } else {
        p1 = iso(x, 0, y);
        p2 = iso(x + width, 0, y);
        p3 = iso(x + width, 0, y + length);
        p4 = iso(x, 0, y + length);
      }

      const p5 = iso(x, levels, y);
      const p6 = iso(x + width, levels, y);
      const p7 = iso(x + width, levels, y + length);
      const p8 = iso(x, levels, y + length);

      //Layers for each shelf level
      if (color === 240) {
        for (let level = 40; level < levels; level += 40) {
          const p5Level = iso(x, level, y);
          const p6Level = iso(x + width, level, y);
          const p7Level = iso(x + width, level, y + length);
          const p8Level = iso(x, level, y + length);
          ctx.beginPath();
          ctx.strokeStyle = `hsl(${color}, 100%, 50%, 0.13)`;
          ctx.fillStyle = `hsl(${color}, 100%, 15%, 0.13)`;
          ctx.lineWidth = 2;
          ctx.moveTo(p5Level.x, p5Level.y);
          ctx.lineTo(p6Level.x, p6Level.y);
          ctx.lineTo(p7Level.x, p7Level.y);
          ctx.lineTo(p8Level.x, p8Level.y);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }
      }

      // Define gradient color for faces
      const startColor = `hsl(${color}, 75%, 95%, 0.3)`;
      const topGradient = ctx.createLinearGradient(p5.x, p5.y, p7.x, p7.y);
      topGradient.addColorStop(0, startColor);
      topGradient.addColorStop(1, `hsl(${color}, 90%, 65%, ${transparency})`);

      // Draw the name on the top face
      ctx.fillStyle = "hsl(160, 95%, 75%)"; // Text color
      ctx.font = "1000 56px Roboto, sans-serif"; // Use Roboto font
      ctx.textAlign = "center"; // Center the text horizontally
      ctx.textBaseline = "middle"; // Center the text vertically
      const centerX = (p5.x + p7.x) / 2;
      const centerY = (p5.y + p7.y) / 2;
      ctx.fillText(name, centerX, centerY);

      //Top face
      ctx.beginPath();
      ctx.lineWidth = 3;
      ctx.strokeStyle = `hsl(${color}, 100%, 50%, ${transparency})`;
      ctx.fillStyle = topGradient;
      ctx.moveTo(p5.x, p5.y);
      ctx.lineTo(p6.x, p6.y);
      ctx.lineTo(p7.x, p7.y);
      ctx.lineTo(p8.x, p8.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Right face (visible if width is positive)
      if (width > 0) {
        ctx.beginPath();
        ctx.lineWidth = 3;
        ctx.strokeStyle = `hsl(${color}, 100%, 50%, ${transparency})`;
        ctx.fillStyle = topGradient;
        ctx.moveTo(p2.x, p2.y);
        ctx.lineTo(p3.x, p3.y);
        ctx.lineTo(p7.x, p7.y);
        ctx.lineTo(p6.x, p6.y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
      // Front face (visible if length is positive)
      if (length > 0) {
        ctx.beginPath();
        ctx.lineWidth = 3;
        ctx.strokeStyle = `hsl(${color}, 100%, 50%, ${transparency})`;
        ctx.fillStyle = topGradient; // Right face
        ctx.moveTo(p4.x, p4.y);
        ctx.lineTo(p3.x, p3.y);
        ctx.lineTo(p7.x, p7.y);
        ctx.lineTo(p8.x, p8.y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }

      // Draw Edges bottom, left, and back (dashed lines)
      ctx.beginPath();
      ctx.lineWidth = 4;
      ctx.strokeStyle = `hsl(${color}, 100%, 50%, ${transparency})`;
      ctx.setLineDash([5, 5]); // Set to dashed line
      ctx.moveTo(p2.x, p2.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(p4.x, p4.y);
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p5.x, p5.y);
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
    },
    []
  );

  const drawRectangle = useCallback(
    (ctx: CanvasRenderingContext2D, rect: Rectangle) => {
      ctx.save();
      ctx.fillStyle =
        rect === selectedRectangle
          ? "hsl(180, 100%, 20%, 0.3)"
          : "rgba(0, 0, 0, 0.1)";
      ctx.lineWidth = rect === selectedRectangle ? 3 : 2;
      ctx.beginPath();
      ctx.rect(rect.startX, rect.startY, rect.width, rect.length);
      ctx.fill();
      ctx.stroke();

      // Draw the name in the center of the rectangle
      ctx.fillStyle = "	hsl(156, 100%, 50%)";
      ctx.font = "24px Arial strong";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const centerX = rect.startX + rect.width / 2;
      const centerY = rect.startY + rect.length / 2;
      ctx.fillText(rect.name, centerX, centerY);
      ctx.restore();
    },
    [selectedRectangle]
  );

  const drawRectangles = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!is3DMode) {
        //Draw 2D rectangles
        hubsRect.forEach((rect) => {
          ctx.strokeStyle = "hsl(168, 100%, 50%)";
          ctx.lineWidth = 4;
          drawRectangle(ctx, rect);
        });
        shelvesRect.forEach((rect) => {
          ctx.strokeStyle = "hsl(240, 100%, 50%)";
          ctx.lineWidth = 3;
          drawRectangle(ctx, rect);
        });
        binsRect.forEach((rect) => {
          ctx.strokeStyle = "hsl(330, 100%, 40%)";
          ctx.lineWidth = 2;
          drawRectangle(ctx, rect);
        });
      } else {
        const sortedHubsRect = sortRectangles(hubsRect);
        const sortedShelvesRect = sortRectangles(shelvesRect);
        const sortedBinsRect = sortRectangles(binsRect);
        //Draw 3D rectangles
        sortedBinsRect.forEach(
          ({ startX, startY, width, length, levels, name }) => {
            draw3DRectangles(
              ctx,
              startX + 15,
              startY + 15,
              width - 30,
              length - 30,
              levels * 40 - 10,
              name,
              330,
              1
            );
          }
        );
        sortedShelvesRect.forEach(
          ({ startX, startY, width, length, levels, name }) => {
            draw3DRectangles(
              ctx,
              startX,
              startY,
              width,
              length,
              levels * 40,
              name,
              240,
              0.2
            );
          }
        );
        sortedHubsRect.forEach(
          ({ startX, startY, width, length, levels, name }) => {
            draw3DRectangles(
              ctx,
              startX,
              startY,
              width,
              length,
              levels * 40,
              name,
              168,
              0.15
            );
          }
        );
      }
    },
    [binsRect, draw3DRectangles, drawRectangle, hubsRect, is3DMode, shelvesRect]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = container.clientWidth * 2;
    canvas.height = container.clientHeight * 2;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvasOffset.x, canvasOffset.y);
    if (!is3DMode) {
      const zoomFactor = 2;
      ctx.scale(zoomFactor, zoomFactor);
      // Create a gradient for a metallic effect
      createGradientBackground(canvas, ctx);
      // Draw 2D rectangles
      drawRectangles(ctx);
    } else {
      ctx.save();

      // Set the isometric projection with zoom-out effect
      const zoomFactor = 0.3;
      ctx.translate(canvas.width / 2, canvas.height / 5);
      ctx.scale(zoomFactor, zoomFactor); // Apply zoom-out by scaling down
      const gridExtent = 5500;

      // Isometric projection constants
      const isoAngle = Math.PI / 6;
      const cos = Math.cos(isoAngle);
      const sin = Math.sin(isoAngle);
      // Helper function to convert 3D coordinates to 2D isometric coordinates
      const iso = (x: number, y: number, z: number) => ({
        x: (x - z) * cos,
        y: y + (x + z) * sin - 350,
      });
      // Starting point
      const start = iso(0, 0, 0);
      // Draw the base plane (x-y plane where z = 0)
      const baseP2 = iso(gridExtent, 0, 0);
      const baseP3 = iso(gridExtent, 0, gridExtent);
      const baseP4 = iso(0, 0, gridExtent);

      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(baseP2.x, baseP2.y);
      ctx.lineTo(baseP3.x, baseP3.y);
      ctx.lineTo(baseP4.x, baseP4.y);
      ctx.closePath();

      // Apply the same gradient as in 2D mode
      const planeGradient = ctx.createRadialGradient(
        start.x,
        start.y,
        100,
        start.x,
        start.y,
        gridExtent
      );
      planeGradient.addColorStop(0, "hsl(210, 30%, 20%)"); // Bright center
      planeGradient.addColorStop(0.5, "hsl(210, 90%, 30%)"); // Metallic blue
      planeGradient.addColorStop(1, "hsl(210, 100%, 100%)"); // Deep black-blue edge
      ctx.fillStyle = planeGradient;
      ctx.fill();

      // Draw grid lines on the base plane
      ctx.strokeStyle = "rgba(100, 100, 100, 0.3)"; // Light grid lines
      ctx.lineWidth = 2;
      const gridSize = 100;
      for (let x = 0; x <= gridExtent; x += gridSize) {
        const lineStart = iso(x, 0, 0);
        const lineEnd = iso(x, 0, gridExtent);
        ctx.beginPath();
        ctx.moveTo(lineStart.x, lineStart.y);
        ctx.lineTo(lineEnd.x, lineEnd.y);
        ctx.stroke();
      }
      for (let y = 0; y <= gridExtent; y += gridSize) {
        const lineStart = iso(0, 0, y);
        const lineEnd = iso(gridExtent, 0, y);
        ctx.beginPath();
        ctx.moveTo(lineStart.x, lineStart.y);
        ctx.lineTo(lineEnd.x, lineEnd.y);
        ctx.stroke();
      }

      // X-axis
      ctx.beginPath();
      ctx.strokeStyle = "hsl(180, 100%, 35%)";
      ctx.lineWidth = 2;
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(start.x + gridExtent * cos, start.y + gridExtent * sin);
      ctx.stroke();

      // Y-axis
      ctx.beginPath();
      ctx.strokeStyle = "hsl(168, 50%, 50%)";
      ctx.lineWidth = 2;
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(start.x, start.y - gridExtent);
      ctx.stroke();

      // Z-axis
      ctx.beginPath();
      ctx.strokeStyle = "hsl(168, 100%, 35%)";
      ctx.lineWidth = 2;
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(start.x - gridExtent * cos, start.y + gridExtent * sin);
      ctx.stroke();

      // Draw 3D rectangles
      drawRectangles(ctx);
      ctx.restore();
    }
  }, [
    binsRect,
    canvasOffset.x,
    canvasOffset.y,
    drawRectangles,
    hubsRect,
    is3DMode,
    shelvesRect,
  ]);

  const findSelectedRect = (
    roundedStartX: number,
    roundedStartY: number,
    rectangles: Rectangle[]
  ) => {
    return rectangles.find(
      (rectangle) =>
        roundedStartX >= rectangle.startX &&
        roundedStartX <= rectangle.startX + rectangle.width &&
        roundedStartY >= rectangle.startY &&
        roundedStartY <= rectangle.startY + rectangle.length
    );
  };

  const handlePanStart = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setPanStart({ x: e.clientX, y: e.clientY });
    setMouseDown(true);
  };
  const handlePanMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (mouseDown) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setCanvasOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setPanStart({ x: e.clientX, y: e.clientY });
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.translate(dx, dy);
        }
      }
    }
  };
  const handlePanEnd = () => {
    setMouseDown(false);
  };

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (cursorMode === "pan") {
        return handlePanStart(e);
      }
      if (cursorMode !== "draw" || is3DMode) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { positionX, positionY } = roundedPosition(e);

      const rectanglesConfig = {
        hub: { rectangles: hubsRect, control_message: null },
        shelf: {
          rectangles: shelvesRect,
          control_message: "Please Shelves must be drawn inside a Hub",
        },
        bin: {
          rectangles: binsRect,
          control_message: "Please Bins must be drawn inside a Shelf",
        },
      };
      const Rectconfig = rectanglesConfig[rectMode];
      const parent =
        rectMode !== "hub"
          ? findSelectedRect(
            positionX,
            positionY,
            rectanglesConfig[rectMode === "shelf" ? "hub" : "shelf"]
              .rectangles
          )
          : null;
      const clickedRectangle =
        rectMode !== "bin"
          ? findSelectedRect(positionX, positionY, Rectconfig.rectangles)
          : null;

      if (clickedRectangle) {
        setSelectedRectangle(clickedRectangle);
        setActiveInput(true);
        setIsDrawing(false);
      } else {
        if (Rectconfig.control_message && !parent) {
          setIsDrawing(false);
          message.warning(Rectconfig.control_message);
        } else {
          setIsDrawing(true);
          setStartX(positionX);
          setStartY(positionY);
          setParentRect(parent!);
        }
        setSelectedRectangle(null);
      }
      setNewRectangle(null);
    },
    [
      binsRect,
      cursorMode,
      hubsRect,
      is3DMode,
      rectMode,
      roundedPosition,
      shelvesRect,
    ]
  );

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!is3DMode && cursorMode === "draw") {
      if (!isDrawing) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const { positionX, positionY } = roundedPosition(e);
      const width = positionX - startX;
      const height = positionY - startY;

      createGradientBackground(canvas, ctx);
      // Draw all existing rectangles
      drawRectangles(ctx);

      //Draw current rectangle
      if (rectMode === "hub") {
        ctx.lineWidth = 5;
        ctx.strokeStyle = "hsl(168, 100%, 50%)";
      } else if (rectMode === "shelf") {
        ctx.lineWidth = 4;
        ctx.strokeStyle = "	hsl(240, 100%, 50%)";
      } else if (rectMode === "bin") {
        ctx.lineWidth = 1;
        ctx.strokeStyle = "hsl(330, 100%, 40%)";
      }
      ctx.beginPath();
      ctx.rect(startX, startY, width, height);

      ctx.stroke();
    } else if (cursorMode === "pan") {
      handlePanMove(e);
    }
  };

  const onMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!is3DMode && isDrawing && cursorMode === "draw") {
        setIsDrawing(false);
        const { positionX, positionY } = roundedPosition(e);
        let width = positionX - startX;
        let length = positionY - startY;
        let newX;
        let newY;
        // Transform values in case we draw the rectangle from right to left
        if (width < 0) {
          newX = positionX;
          setStartX(() => positionX);
          width = -width;
        }
        if (length < 0) {
          newY = positionY;
          setStartY(() => positionY);
          length = -length;
        }
        const currentRectangle = {
          startX: newX ? newX : startX,
          startY: newY ? newY : startY,
          width: width,
          length: length,
          levels: 1,
          name: "",
        };
        setNewRectangle(currentRectangle);
        setActiveInput(true);
        setSelectedRectangle(null);
      } else if (cursorMode === "pan") {
        handlePanEnd();
      }
    },
    [cursorMode, is3DMode, isDrawing, roundedPosition, startX, startY]
  );

  const onSaveShelf = useCallback(
    (updatedRectangle: any): void => {
      if (selectedRectangle) {
        if (rectMode === "hub") {
          setHubsRect((prevRect) =>
            prevRect.map((r) =>
              r === selectedRectangle ? updatedRectangle : r
            )
          );
        }
        if (rectMode === "shelf") {
          setShelvesRect((prevRect) =>
            prevRect.map((r) =>
              r === selectedRectangle ? updatedRectangle : r
            )
          );
        }
      } else {
        if (rectMode === "hub") {
          setHubsRect((prevRect) => [...prevRect, updatedRectangle]);

        }
        if (rectMode === "shelf") {
          setShelvesRect((prevRect) => [...prevRect, updatedRectangle]);
        }
        if (rectMode === "bin") {
          setBinsRect((prevRect) => [...prevRect, updatedRectangle]);
        }
      }
      console.log('hubsRect', hubsRect)
      setNewRectangle(null);
      setActiveInput(false);
      setIsDrawing(false);
    },
    [rectMode, selectedRectangle]
  );

  const closeShelfModal = useCallback(() => {
    setActiveInput(false);
    setNewRectangle(null); // Reset the new rectangle
    setSelectedRectangle(null);
  }, []);

  const toggleViewMode = () => {
    setIs3DMode(!is3DMode);
  };

  const toggleDrawingMode = () => {
    if (cursorMode === "pan") {
      setCursorMode("draw");
    }
    if (cursorMode === "draw") {
      setCursorMode("pan");
    }
    setActiveInput(false);
    setNewRectangle(null);
    setIsDrawing(false);
  };

  const undoRect = () => {
    if (rectMode === "hub" && hubsRect.length > 0) setHubsRect((prevRect) => prevRect.slice(0, -1));
    if (rectMode === "shelf" && shelvesRect.length > 0) setShelvesRect((prevRect) => prevRect.slice(0, -1));
    if (rectMode === "bin" && binsRect.length > 0) setBinsRect((prevRect) => prevRect.slice(0, -1));
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        position: "relative",
        width: "100%",
        height: "1600px",
      }}
    >
      <canvas
        style={{ border: "1px solid #000" }}
        ref={canvasRef}
        width={"100%"}
        height={"100vh"}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      ></canvas>
      <Div>
        <StyledSimpleButton text="Move Display"
          isActive={cursorMode === "pan"}
          onClick={toggleDrawingMode}
        />
        <StyledSimpleButton text="+ Add WareHouse"
          isActive={rectMode === "hub"}
          onClick={() => {
            setRectMode("hub");
            setCursorMode("draw");
          }}
        />
        <StyledSimpleButton text="+ Add Shelf"
          isActive={rectMode === "shelf"}
          onClick={() => {
            setRectMode("shelf");
            setCursorMode("draw");
          }}
        />
        <StyledSimpleButton text="+ Add Bin"
          isActive={rectMode === "bin"}
          onClick={() => {
            setRectMode("bin");
            setCursorMode("draw");
          }}
        />
        <button
          onClick={undoRect}
        ><img src={UndoIcon} style={{ width: '30px', height: '30px' }} /></button>
      </Div>
      <SimpleButton style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1,
      }} text="2D / 3D" onClick={toggleViewMode} />
      {
        activeInput && (newRectangle || selectedRectangle) && (
          <AddRect
            activeInput={activeInput}
            rectangle={selectedRectangle || newRectangle!}
            parent={parentRect!}
            rectangleMode={rectMode}
            onSave={onSaveShelf}
            onClose={closeShelfModal}
          ></AddRect>
        )
      }
    </div >
  );
}

const Div = styled.div`
  position: absolute;
  display: flex;
  justify-content: space-around;
  margin-right: 8px;
  top: 10px;
  left: 10px;
  width: 35%;
  z-index: 1;
`;

const StyledSimpleButton = styled(SimpleButton)`
  ${props => props.isActive && `
    background: hsl(168, 100%, 40%) !important;
  `}
`;

export default CrossDocking;
